import { useEffect, useState } from 'react';
import { useConsentStore, type ConsentPreferences } from '../store/consentStore';
import { Link } from 'react-router-dom';

export const ConsentBanner = () => {
    const { status, preferences, setConsent, acceptAll, rejectAll } = useConsentStore();
    const [isVisible, setIsVisible] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [tempPreferences, setTempPreferences] = useState<ConsentPreferences>(preferences);

    useEffect(() => {
        // Show banner if consent is pending
        setIsVisible(status === 'pending');
        // Reset details view when banner visibility changes
        if (status !== 'pending') {
            setShowDetails(false);
        }
    }, [status]);

    useEffect(() => {
        // Sync temp preferences when store preferences change
        setTempPreferences(preferences);
    }, [preferences]);

    const handleAcceptAll = () => {
        acceptAll();
        setIsVisible(false);
    };

    const handleRejectAll = () => {
        rejectAll();
        setIsVisible(false);
    };

    const handleSavePreferences = () => {
        setConsent(tempPreferences);
        setIsVisible(false);
    };

    const handleToggleAnalytics = () => {
        setTempPreferences({
            ...tempPreferences,
            analytics: !tempPreferences.analytics
        });
    };

    const handleToggleMarketing = () => {
        setTempPreferences({
            ...tempPreferences,
            marketing: !tempPreferences.marketing
        });
    };

    if (!isVisible) {
        return null;
    }

    return (
        <div className="consent-banner" role="dialog" aria-label="Cookie consent" aria-modal="false">
            <div className="consent-banner-content">
                <div className="consent-banner-main">
                    <div className="consent-banner-text">
                        <h3 className="consent-banner-title">We value your privacy</h3>
                        <p className="consent-banner-description">
                            We use cookies and similar technologies to analyze site usage, improve our services, and personalize content. 
                            You can choose to accept all, reject all, or customize your preferences.
                        </p>
                    </div>
                    <div className="consent-banner-actions">
                        {!showDetails ? (
                            <>
                                <button
                                    className="consent-button consent-button-secondary"
                                    onClick={handleRejectAll}
                                    aria-label="Reject all cookies"
                                >
                                    Reject All
                                </button>
                                <button
                                    className="consent-button consent-button-link"
                                    onClick={() => setShowDetails(true)}
                                    aria-label="Customize cookie preferences"
                                >
                                    Customize
                                </button>
                                <button
                                    className="consent-button consent-button-primary"
                                    onClick={handleAcceptAll}
                                    aria-label="Accept all cookies"
                                >
                                    Accept All
                                </button>
                            </>
                        ) : (
                            <div className="consent-details">
                                <div className="consent-preference">
                                    <label className="consent-preference-label">
                                        <input
                                            type="checkbox"
                                            checked={tempPreferences.analytics}
                                            onChange={handleToggleAnalytics}
                                            className="consent-checkbox"
                                        />
                                        <div className="consent-preference-content">
                                            <span className="consent-preference-title">Analytics Cookies</span>
                                            <span className="consent-preference-description">
                                                Help us understand how visitors interact with our website
                                            </span>
                                        </div>
                                    </label>
                                </div>
                                <div className="consent-preference">
                                    <label className="consent-preference-label">
                                        <input
                                            type="checkbox"
                                            checked={tempPreferences.marketing}
                                            onChange={handleToggleMarketing}
                                            className="consent-checkbox"
                                        />
                                        <div className="consent-preference-content">
                                            <span className="consent-preference-title">Marketing Cookies</span>
                                            <span className="consent-preference-description">
                                                Used to deliver personalized advertisements
                                            </span>
                                        </div>
                                    </label>
                                </div>
                                <div className="consent-details-actions">
                                    <button
                                        className="consent-button consent-button-link"
                                        onClick={() => setShowDetails(false)}
                                    >
                                        Back
                                    </button>
                                    <button
                                        className="consent-button consent-button-primary"
                                        onClick={handleSavePreferences}
                                    >
                                        Save Preferences
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="consent-banner-links">
                    <Link to="/privacy" className="consent-link">
                        Privacy Policy
                    </Link>
                </div>
            </div>
        </div>
    );
};

